<template>
  <v-dialog :value="isOpened" max-width="640px" @input="close">
    <v-card>
      <v-toolbar color="primary" dark>
        <v-toolbar-title>
          {{ $t('entities.stockTransactions.entity') }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="py-4">
        <v-simple-table>
          <tbody>
            <tr v-for="row in detailRows" :key="row.key">
              <td class="font-weight-medium text-no-wrap pr-4">
                {{ row.label }}
              </td>
              <td>
                <template v-if="row.key === 'type'">
                  <v-chip small :color="getTransactionTypeColor(transaction)">
                    {{ transactionTypeLabel }}
                  </v-chip>
                </template>
                <template v-else-if="row.key === 'variant'">
                  <div class="d-flex align-center">
                    <img
                      v-if="variantImage"
                      :src="variantImage"
                      alt="Variant"
                      class="variant-preview mr-3" />
                    <div>
                      {{ row.value }}
                    </div>
                  </div>
                </template>
                <template v-else-if="row.key === 'files'">
                  <div class="d-flex flex-wrap my-1">
                    <v-chip
                      v-for="(file, index) in transactionFiles"
                      :key="`file-${index}`"
                      small
                      class="my-1 mr-1"
                      @click="downloadFile(file)">
                      {{ file }}
                    </v-chip>
                    <span v-if="!transactionFiles.length">{{ row.value }}</span>
                  </div>
                </template>
                <template v-else>
                  {{ row.value }}
                </template>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
      <v-divider />
      <v-card-actions class="py-4">
        <v-spacer />
        <v-btn text color="primary" @click="close">
          {{ $t('miscellaneous.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TRANSACTION_TYPE_COLORS from './colors'
import FilesApiV2 from '@/services/misc/FilesApiV2'

const WASTE_CODES = ['WASTE', 'RESOLVE_WASTE']

export default {
  name: 'StockTransactionDetailsDialog',
  props: {
    isOpened: { type: Boolean, default: false },
    transaction: { type: Object, default: () => ({}) },
    suppliers: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    invoices: { type: Array, default: () => [] },
    arenaId: { type: [String, Number], default: null },
  },
  computed: {
    detailRows() {
      return [
        {
          key: 'type',
          label: this.$t('entities.stockTransactions.transactionType'),
          value: this.transactionTypeLabel,
        },
        this.variantRow,
        {
          key: 'quantity',
          label: this.$t('generalAttributes.quantity'),
          value: this.transactionQuantity,
        },
        ...this.stockLevelRows,
        {
          key: 'user',
          label: this.$t('entities.users.entity'),
          value: this.userLabel,
        },
        {
          key: 'supplier',
          label: this.$t('entities.suppliers.entity'),
          value: this.supplierLabel,
        },
        {
          key: 'invoice',
          label: this.$t('entities.invoices.entity'),
          value: this.invoiceLabel,
        },
        {
          key: 'description',
          label: this.$t('miscellaneous.description'),
          value: this.transaction?.description || '-',
        },
        {
          key: 'files',
          label: this.$t('miscellaneous.files'),
          value: this.transactionFiles.join(', ') || '-',
        },
        {
          key: 'created_at',
          label: this.$t('miscellaneous.date'),
          value: this.formatDateValue(this.transaction?.created_at),
        },
      ]
    },
    transactionTypeLabel() {
      const code = this.transaction?.type?.code
      return code ? this.$t(`entities.stockTransactions.types.${code}`) : '-'
    },
    variantRow() {
      return {
        key: 'variant',
        label: this.$t('entities.products.variant'),
        value: this.transaction?.arena_variant?.name,
      }
    },
    variantImage() {
      return this.transaction?.arena_variant?.image || ''
    },
    stockLevelRows() {
      const stockLevelRows = [
        {
          key: 'available_before',
          label: this.$t('entities.stockTransactions.availableStockBefore'),
          value: this.formatNumber(this.transaction?.available_stock_before),
        },
        {
          key: 'available_after',
          label: this.$t('entities.stockTransactions.availableStockAfter'),
          value: this.formatNumber(this.transaction?.available_stock_after),
        },
      ]
      if (this.isWasteTransaction) {
        return stockLevelRows.concat([
          {
            key: 'waste_before',
            label: this.$t('entities.stockTransactions.wasteStockBefore'),
            value: this.formatNumber(this.transaction?.waste_stock_before),
          },
          {
            key: 'waste_after',
            label: this.$t('entities.stockTransactions.wasteStockAfter'),
            value: this.formatNumber(this.transaction?.waste_stock_after),
          },
        ])
      }
      return stockLevelRows
    },
    isWasteTransaction() {
      const code = this.transaction?.type?.code
      return WASTE_CODES.includes(code)
    },
    supplierLabel() {
      const supplierId = this.transaction?.supplier_id
      if (!supplierId) return '-'
      const supplier = this.suppliers.find((it) => it.id === supplierId)
      return supplier?.name || '-'
    },
    userLabel() {
      if (!Array.isArray(this.users)) return '-'
      const user = this.users.find((it) => it.id === this.transaction?.user_id)
      return user?.username || '-'
    },
    invoiceLabel() {
      const invoiceId = this.transaction?.invoice_id
      if (!invoiceId) return '-'
      const invoice = this.invoices.find((it) => it.id === invoiceId)
      return invoice?.number || '-'
    },
    transactionQuantity() {
      const code = this.transaction?.type?.code
      if (!code) return '-'
      const diff = WASTE_CODES.includes(code)
        ? (this.transaction?.waste_stock_after || 0) -
          (this.transaction?.waste_stock_before || 0)
        : (this.transaction?.available_stock_after || 0) -
          (this.transaction?.available_stock_before || 0)
      return this.formatNumber(Math.abs(diff))
    },
    transactionFiles() {
      return Array.isArray(this.transaction?.files)
        ? this.transaction.files
        : []
    },
    downloadArenaId() {
      return (
        this.arenaId ||
        this.transaction?.arena_id ||
        this.transaction?.arena?.id ||
        null
      )
    },
  },
  methods: {
    getTransactionTypeColor(transaction) {
      const code = transaction?.type?.code
      return code
        ? TRANSACTION_TYPE_COLORS[code]
        : TRANSACTION_TYPE_COLORS.DEFAULT
    },
    formatDateValue(value) {
      if (!value) return '-'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return value
      return parsed.toLocaleString()
    },
    close() {
      this.$emit('dialog:close')
    },
    formatNumber(value) {
      if (typeof value !== 'number' || Number.isNaN(value)) return '-'
      return value.toLocaleString()
    },
    async downloadFile(filename) {
      if (!filename) return
      const arenaId = this.downloadArenaId
      if (!arenaId) {
        console.warn('Missing arena id for file download')
        return
      }
      try {
        const { data } = await FilesApiV2.download(String(arenaId), filename)
        const url = data?.url
        if (url) {
          this.triggerDownload(url, filename)
        } else {
          throw new Error('missing_download_url')
        }
      } catch (error) {
        console.error('Failed to download file', error)
      }
    },
    triggerDownload(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      if (filename) link.download = filename
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.variant-preview {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
